import { getAddress } from 'ethers';
import { Reducer } from 'redux';

import { changeUser } from '#/reduxState/currentUser/currentUserReducer';

import { Wallet, EMPTY_WALLET } from './walletUtils';

// Actions

export type changeAnyWallet = { type: 'CHANGE_WALLET' };

export function setPersonalWalletSecret(
  personalWalletSecret: string | undefined
) {
  return {
    type: 'SET_PERSONAL_WALLET_SECRET' as const,
    personalWalletSecret,
  };
}

export const changePrivateKeyWallet = ({
  wallet,
  privateKeyEncrypted,
  unlockedByKeystoreFile,
  personalWalletSecret,
}: {
  wallet: string;
  privateKeyEncrypted: string;
  unlockedByKeystoreFile: boolean;
  personalWalletSecret: string;
}) =>
  ({
    type: 'CHANGE_WALLET',
    subType: 'CHANGE_PRIVATE_KEY_WALLET',
    wallet,
    privateKeyEncrypted,
    // "undefined" can be only dispatched from old cached apps
    privateKeyPersonalEncryption: 'personal1' as undefined | 'personal1',
    unlockedByKeystoreFile,
    personalWalletSecret,
  }) as const;

export const changeSoftwareWallet = ({
  wallet,
  walletType,
  personalWalletSecret,
}: {
  wallet: string;
  personalWalletSecret?: string;
  walletType: 'softwareWeb3' | 'coinbaseWallet' | 'walletConnect' | 'qrSynced';
}) =>
  ({
    type: 'CHANGE_WALLET',
    subType: 'CHANGE_SOFTWARE_WALLET',
    walletType,
    wallet,
    personalWalletSecret,
  }) as const;

export const changeSoftwareDiscoveredWallet = ({
  wallet,
  walletType,
  personalWalletSecret,
  walletDiscoveredRdns,
}: {
  wallet: string;
  personalWalletSecret?: string;
  walletDiscoveredRdns: string;
  walletType: 'softwareDiscovered';
}) =>
  ({
    type: 'CHANGE_WALLET',
    subType: 'CHANGE_SOFTWARE_DISCOVERED_WALLET',
    walletType,
    wallet,
    personalWalletSecret,
    walletDiscoveredRdns,
  }) as const;

// export const changeWalletConnectWallet = (
//   wallet: string,
//   useWalletRpc: boolean
// ) =>
//   ({
//     type: 'CHANGE_WALLET',
//     subType: 'CHANGE_WALLET_CONNECT_WALLET',
//     wallet,
//     walletType: 'walletConnect',
//     useWalletRpc,
//   } as const);

// export const changeHardwareWallet = ({
//   wallet,
//   walletType,
//   path,
//   personalWalletSecret,
// }: {
//   wallet: string;
//   walletType: 'trezor' | 'ledger';
//   path: string;
//   personalWalletSecret?: string;
// }) =>
//   ({
//     type: 'CHANGE_WALLET',
//     subType: 'CHANGE_HARDWARE_WALLET',
//     walletType,
//     wallet,
//     path,
//     personalWalletSecret,
//   } as const);

export const removeWallet = () =>
  ({
    type: 'REMOVE_WALLET',
  }) as const;

export type WalletAction = ReturnType<
  | typeof changePrivateKeyWallet
  | typeof changeSoftwareWallet
  | typeof changeSoftwareDiscoveredWallet
  | typeof removeWallet
  | typeof setPersonalWalletSecret
  // User actions
  | typeof changeUser
>;

// Reducer
export type IWallet = Wallet;

export const walletReducer: Reducer<Wallet, WalletAction> = (
  state = EMPTY_WALLET,
  action
): Wallet => {
  switch (action.type) {
    case 'CHANGE_USER':
      if (action.loggedIn === false) {
        // Note that when user is removed, eg. session removed/expired/logged out in another tab,
        // we want to remove user wallet too...
        return {
          ...EMPTY_WALLET,
          updated: Date.now(),
        };
      }
      return state;
    case 'SET_PERSONAL_WALLET_SECRET':
      return {
        ...state,
        personalWalletSecret: action.personalWalletSecret,
        updated: Date.now(),
      };
    case 'CHANGE_WALLET': {
      switch (action.subType) {
        case 'CHANGE_PRIVATE_KEY_WALLET':
          return {
            updated: Date.now(),
            walletAddress: action.wallet.toLowerCase(),
            walletAddressOriginal: getAddress(action.wallet),
            walletType: action.unlockedByKeystoreFile
              ? 'keystore'
              : 'privateKey',
            privateKeyEncrypted: action.privateKeyEncrypted,
            privateKeyPersonalEncryption: action.privateKeyPersonalEncryption,
            personalWalletSecret: action.personalWalletSecret,
          };
        case 'CHANGE_SOFTWARE_DISCOVERED_WALLET':
          return {
            updated: Date.now(),
            walletAddress: action.wallet.toLowerCase(),
            walletAddressOriginal: getAddress(action.wallet),
            walletType: action.walletType,
            personalWalletSecret: action.personalWalletSecret,
            walletDiscoveryRdns: action.walletDiscoveredRdns,
          };
        case 'CHANGE_SOFTWARE_WALLET':
          return {
            updated: Date.now(),
            walletAddress: action.wallet.toLowerCase(),
            walletAddressOriginal: getAddress(action.wallet),
            walletType: action.walletType,
            personalWalletSecret: action.personalWalletSecret,
          };
        // case 'CHANGE_WALLET_CONNECT_WALLET':
        //   return {
        //     updated: Date.now(),
        //     walletAddress: action.wallet.toLowerCase(),
        //     walletAddressOriginal: getAddress(action.wallet),
        //     walletType: action.walletType,
        //     useWalletRpc: action.useWalletRpc,
        //   };
        // case 'CHANGE_HARDWARE_WALLET':
        //   return {
        //     updated: Date.now(),
        //     walletAddress: action.wallet.toLowerCase(),
        //     walletAddressOriginal: getAddress(action.wallet),
        //     walletType: action.walletType,
        //     hardwareWalletPath: action.path,
        //     personalWalletSecret: action.personalWalletSecret,
        //   };
        default:
          return state;
      }
    }
    case 'REMOVE_WALLET':
      return {
        updated: Date.now(),
        walletType: null,
        walletAddress: '',
        walletAddressOriginal: '',
      };
    default:
      return state;
  }
};
